/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useState, type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { EuiBasicTable } from '@elastic/eui'

import useGetCostsInstances from '@modules/billing-lib/queries/useGetCostsInstances'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'
import useGetBillingCloudResources from '@modules/billing-lib/queries/useGetBillingCloudResources'

import useColumns from './useColumns'
import DeleteBudgetModal from './DeleteBudgetModal'
import BudgetDetails from './BudgetDetails'
import { getCurrentMonthDates } from './utils'
import useTableContent from './useTableContent'

import type { ActionError, Budget } from '../types'

type Props = {
  budgets: Budget[]
  onActionError: (error: ActionError) => void
  onCleanActionError: (budgetId: Budget['id']) => void
}

const BudgetsTable: FunctionComponent<Props> = ({ budgets, onActionError, onCleanActionError }) => {
  const [isBudgetDetailsOpen, setIsBudgetDetailsOpen] = useState(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null)
  const { formatMessage } = useIntl()

  const organizationId = useOrganizationId()

  const { data: cloudResources } = useGetBillingCloudResources(organizationId)

  const { data: costs } = useGetCostsInstances(
    {
      pathParameters: { organization_id: organizationId },
      queryParameters: getCurrentMonthDates(),
    },
    {
      enabled: Boolean(organizationId),
    },
  )

  const handleViewBudgetDetails = (budget: Budget) => {
    setIsBudgetDetailsOpen(true)
    setSelectedBudget(budget)
  }

  const handleDeleteBudgetIntent = (budget: Budget) => {
    setIsDeleteConfirmationOpen(true)
    setSelectedBudget(budget)
  }

  const { columns, onDeleteConfirmed } = useColumns({
    cloudResources,
    costs,
    onViewDetails: handleViewBudgetDetails,
    onDeleteIntent: handleDeleteBudgetIntent,
    onActionError,
    onCleanActionError,
  })

  const { pageOfItems, pagination, sorting, handleTableChange } = useTableContent({
    budgets,
    cloudResources,
    costs,
  })

  const handleDeleteCancel = () => {
    setIsDeleteConfirmationOpen(false)
    setSelectedBudget(null)
  }

  const handleDeleteConfirm = () => {
    setIsDeleteConfirmationOpen(false)

    if (selectedBudget) {
      onDeleteConfirmed(selectedBudget)
      setSelectedBudget(null)
    }
  }

  const handleCloseBudgetDetails = () => {
    setIsBudgetDetailsOpen(false)
    setSelectedBudget(null)
  }

  return (
    <Fragment>
      <EuiBasicTable
        items={pageOfItems}
        columns={columns}
        tableCaption={formatMessage({
          id: 'billing.notifications.budgets.table.caption',
          defaultMessage: 'Budgets table',
        })}
        pagination={pagination}
        onChange={handleTableChange}
        sorting={sorting}
      />

      {isBudgetDetailsOpen && selectedBudget && (
        <BudgetDetails
          budget={selectedBudget}
          cloudResources={cloudResources}
          costs={costs}
          onClose={handleCloseBudgetDetails}
        />
      )}

      {isDeleteConfirmationOpen && selectedBudget && (
        <DeleteBudgetModal
          budget={selectedBudget}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Fragment>
  )
}

export default BudgetsTable
